/**
 * The Intake slots as the screen draws them, built from the table the API
 * serves at `GET /api/intake/slots`.
 *
 * The API judges every upload by that same table, so what a slot offers in the
 * file picker and what the server accepts cannot come to differ. The server
 * gives each slot its name, its formats, whether it is required and whether it
 * asks what a file is; the screen adds only its own words about the slot.
 */

import type { components, FileType } from "@patent-studio/api-types";

type SupportingTag = components["schemas"]["SupportingTag"];

/** The table as the API serves it. */
export type IntakeSlotTable = components["schemas"]["IntakeSlots"];

/** What the attorney can say a file in a tagged slot is, as the server names it. */
export interface SlotTag {
  /** The wire value, sent as the upload's `supporting_tag`. */
  id: SupportingTag;
  label: string;
  /** The shortest description the tag accepts, or null where the tag says enough. */
  descriptionMin: number | null;
  /** The file type a file with this tag is stored as. */
  fileType: FileType;
  /** The content types this tag takes, as the rules judge them. */
  takes: readonly string[];
}

export interface IntakeComponentSpec {
  name: string;
  title: string;
  /** One line, written for a patent professional, on what belongs here. */
  description: string;
  required: boolean;
  /** The formats as the attorney reads them, in the table's order. */
  acceptedLabels: readonly string[];
  /** Content type to extensions, as the file picker and react-dropzone take them. */
  accept: Record<string, string[]>;
  fileType: FileType;
  /** How many files the slot may hold, where it holds only one. */
  maxFiles?: number;
  /**
   * What the attorney can say a file here is. A slot with tags takes a file
   * only once one is chosen, since nothing else can classify its files.
   */
  tags: readonly SlotTag[];
}

/** What the screen says about each slot, under its title. */
const SLOT_COPY: Partial<Record<string, string>> = {
  disclosure:
    "The invention as the inventor set it out. Any form: formal disclosure, memo, notes.",
  supporting:
    "Element titles, inventor recordings, follow-up correspondence. Figures, diagrams and images go here too.",
  prior_art:
    "References already known to you. Search reports, cited patents, published applications, product literature.",
};

/**
 * The served table, one spec per slot in the order the server lists them.
 *
 * Every alias the server takes for a format is offered, so a browser that names
 * an M4A file `audio/x-m4a` is not refused here and accepted there.
 */
export function intakeComponents(
  table: IntakeSlotTable,
): IntakeComponentSpec[] {
  const judged = (names: readonly string[]) =>
    names.map((name) => table.formats[name].content_types[0]);
  return table.slots.map((slot) => {
    // What the slot takes is what it or any of its tags takes: a drawing dropped
    // on Supporting Material is a Figures file, which the server stores.
    const names = new Set([
      ...slot.accepts,
      ...slot.tags.flatMap((id) => table.tags[id].accepts ?? []),
    ]);
    const formats = [...names].map((name) => table.formats[name]);
    // The contracts package holds every slot's and tag's file type to the
    // API's FileType, and every tag to SupportingTag, by closure tests.
    const fileType = slot.file_type as FileType;
    return {
      name: slot.slot,
      title: slot.label,
      description: SLOT_COPY[slot.slot] ?? "",
      required: slot.required,
      acceptedLabels: formats.map((format) => format.label),
      accept: Object.fromEntries(
        formats.flatMap((format) =>
          format.content_types.map((type) => [type, format.extensions]),
        ),
      ),
      fileType,
      maxFiles: slot.several ? undefined : 1,
      tags: slot.tags.map((id) => {
        const tag = table.tags[id];
        return {
          id: id as SupportingTag,
          label: tag.label,
          descriptionMin: tag.description_min,
          fileType: (tag.stored_as ?? fileType) as FileType,
          takes: judged(tag.accepts ?? slot.accepts),
        };
      }),
    };
  });
}
